import React, { useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaReceipt, FaSyncAlt } from 'react-icons/fa';
import Modal from '../Modal/Modal';
import { apiHelper } from '../../utils/api';
import { formatDateDisplay, formatTimeDisplay } from '../../utils/date';
import './PaymentHistoryModal.scss';

const formatCurrency = (amount, currency = 'INR') => {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: String(currency || 'INR').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    return `INR ${value.toLocaleString('en-IN')}`;
  }
};

const PaymentHistoryModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const loadPayments = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiHelper.get('/payments/history?limit=100');
      setPayments(Array.isArray(response?.payments) ? response.payments : []);
    } catch (err) {
      console.error('Failed to fetch payment history:', err);
      setError('Unable to load payment records right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadPayments();
  }, [isOpen]);

  const filteredPayments = useMemo(() => {
    if (statusFilter === 'all') return payments;
    return payments.filter((item) => String(item?.status || '').toLowerCase() === statusFilter);
  }, [payments, statusFilter]);

  const counts = useMemo(() => {
    const success = payments.filter((item) => String(item?.status).toLowerCase() === 'success').length;
    const failed = payments.filter((item) => String(item?.status).toLowerCase() === 'failed').length;
    return { all: payments.length, success, failed };
  }, [payments]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Payment Records" size="large">
      <div className="payment-history">
        <div className="payment-history__toolbar">
          <div className="payment-history__filters" role="tablist" aria-label="Payment status filters">
            <button
              type="button"
              className={`payment-history__filter ${statusFilter === 'all' ? 'is-active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({counts.all})
            </button>
            <button
              type="button"
              className={`payment-history__filter ${statusFilter === 'success' ? 'is-active' : ''}`}
              onClick={() => setStatusFilter('success')}
            >
              Success ({counts.success})
            </button>
            <button
              type="button"
              className={`payment-history__filter ${statusFilter === 'failed' ? 'is-active' : ''}`}
              onClick={() => setStatusFilter('failed')}
            >
              Failed ({counts.failed})
            </button>
          </div>

          <button
            type="button"
            className="payment-history__refresh"
            onClick={loadPayments}
            disabled={loading}
            aria-label="Refresh payment history"
            title="Refresh payment history"
          >
            <FaSyncAlt className={loading ? 'is-spinning' : ''} />
            
          </button>
        </div>

        {loading ? (
          <div className="payment-history__state">Loading payment records...</div>
        ) : error ? (
          <div className="payment-history__state payment-history__state--error">{error}</div>
        ) : filteredPayments.length === 0 ? (
          <div className="payment-history__state">No payment records found for this filter.</div>
        ) : (
          <div className="payment-history__list">
            {filteredPayments.map((payment) => {
              const isSuccess = String(payment?.status || '').toLowerCase() === 'success';
              const created = payment?.createdAt ? new Date(payment.createdAt) : null;
              const plan = String(payment?.plan || 'unknown').toUpperCase();

              return (
                <article
                  className={`payment-history__item ${isSuccess ? 'is-success' : 'is-failed'}`}
                  key={payment?._id || `${payment?.orderId}-${payment?.createdAt}`}
                >
                  <header className="payment-history__item-head">
                    <span className="payment-history__badge">
                      {isSuccess ? <FaCheckCircle /> : <FaExclamationCircle />}
                      {isSuccess ? 'Successful' : 'Failed'}
                    </span>
                    <div className="payment-history__amount">
                      <span className="payment-history__amount-label">Amount Paid</span>
                      <span className="payment-history__amount-value">
                        {formatCurrency(payment?.amount, payment?.currency)}
                      </span>
                    </div>
                  </header>

                  <div className="payment-history__meta">
                    <span className="payment-history__plan">{plan} Plan</span>
                    <span className="payment-history__meta-dot" aria-hidden="true">
                      •
                    </span>
                    <span>
                      {created ? formatDateDisplay(created) : 'N/A'}
                      {created ? ` · ${formatTimeDisplay(created)}` : ''}
                    </span>
                  </div>

                  <div className="payment-history__grid">
                    <div>
                      <p className="payment-history__label">Order ID</p>
                      <p className="payment-history__value payment-history__value--mono">{payment?.orderId || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="payment-history__label">Payment ID</p>
                      <p className="payment-history__value payment-history__value--mono">
                        {payment?.paymentId || 'Not generated'}
                      </p>
                    </div>
                    <div>
                      <p className="payment-history__label">Status</p>
                      <p className="payment-history__value">
                        <span className={`payment-history__inline-status ${isSuccess ? 'is-success' : 'is-failed'}`}>
                          {isSuccess ? 'Success' : 'Failed'}
                        </span>
                      </p>
                    </div>
                  </div>

                  {!isSuccess && (
                    <div className="payment-history__failure">
                      <p className="payment-history__failure-title">
                        <FaReceipt /> Failure Details
                      </p>
                      <p className="payment-history__failure-desc">
                        {payment?.failureDescription || 'Payment could not be completed.'}
                        {payment?.failureReason ? ` (${payment.failureReason})` : ''}
                      </p>
                      {payment?.failureCode && (
                        <p className="payment-history__failure-code">Code: {payment.failureCode}</p>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PaymentHistoryModal;
